import { gql } from '@urql/core';
import { Layout } from 'components/common/layout/Layout';
import {
    College_DetailsQuery,
    College_DetailsDocument,
    SsgCollegesBySlugDocument,
    College_DetailsQueryVariables,
    RecordProfileViewDocument,
    RecordProfileViewMutation,
    RecordProfileViewMutationVariables,
    Espin_Role_Enum,
} from 'generated/spin-graphql';
import { CollegeDetails } from 'components/colleges/CollegeDetails';
import { GetStaticProps } from 'next';
import React, { useEffect, useMemo } from 'react';
import { PathsForPage, UrqlSSGForPage } from 'server/utils/serverUtils/ssg.utils';
import { useQuery } from 'urql';
import { GenericFullPageLoading } from '../../../components/common/GenericFullPageLoading';
import { CustomSEO } from '../../../components/common/layout/HeadMeta';
import { useAppState } from 'client/providers/AppProvider';
import { useAuthenticatedMutation } from 'client/hooks';
import Custom404 from '../../404';

gql`
    query College_Details($slug: String!) {
        espin_college(where: { _and: [{ slug: { _eq: $slug } }, { verified: { _eq: true } }] }, limit: 1) {
            id
            ...CollegeDetails
        }
    }
`;

gql`
    mutation RecordParentViewsProfile($input: [espin_profile_view_insert_input!]!) {
        insert_espin_profile_view(objects: $input) {
            returning {
                id
            }
        }
    }
`;

export default function College_Details({ variables }: { variables: College_DetailsQueryVariables }) {
    const [result] = useQuery({
        query: College_DetailsDocument,
        variables,
        context: useMemo(() => ({ additionalTypenames: ['espin_college'] }), []),
    });

    const { user } = useAppState();
    const { espin_college: colleges } = result.data ?? {};
    const [college] = colleges ?? [];
    const [_, recordProfileView] = useAuthenticatedMutation<
        RecordProfileViewMutation,
        RecordProfileViewMutationVariables
    >(RecordProfileViewDocument);

    useEffect(() => {
        if (!result.fetching && !!colleges && !!user) {
            if (colleges.length > 0 && user && user.role === Espin_Role_Enum.EspinPlayerUser) {
                recordProfileView({
                    input: {
                        college_id: colleges[0].id,
                    },
                });
            } else if (colleges.length > 0 && user && user?.role === Espin_Role_Enum.EspinParentUser) {
                recordProfileView({
                    input: {
                        college_id: colleges[0].id,
                        player_user_id: user.parent_profile.child_profiles[0].user_id,
                    },
                });
            } else if (colleges.length > 0 && user && user?.role === Espin_Role_Enum.EspinHighschoolUser) {
                recordProfileView({
                    input: {
                        college_id: colleges[0].id,
                        high_school_id: user.high_school_profile.high_school.id,
                    },
                });
            }
        }
    }, [colleges, recordProfileView, result.fetching, user]);

    const seoOverride: CustomSEO = {
        title: college?.name,
        description: college?.bio,
    };

    if (result.fetching) {
        return <GenericFullPageLoading />;
    }

    if (!!!college) {
        return <Custom404 />;
    }
    return (
        <Layout seoOverride={seoOverride}>
            <CollegeDetails college={college} />
        </Layout>
    );
}

gql`
    query SSGCollegesBySlug {
        espin_college(
            where: { _and: [{ verified: { _eq: true } }, { slug: { _is_null: false } }] }
            order_by: { name: asc }
        ) {
            id
            slug
        }
    }
`;
// need a query for this one.
export async function getStaticPaths() {
    return PathsForPage(SsgCollegesBySlugDocument, 'espin_college', 'slug');
}

export const getStaticProps: GetStaticProps = async (ctx) => {
    return UrqlSSGForPage<College_DetailsQuery>(College_DetailsDocument, ctx.params);
};
